import { useState, useEffect } from 'react';
import axios from 'axios';
import { Upload, UserPlus, Users as UsersIcon, Edit, Trash2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudentManagement = () => {
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', contact: '', class_ids: [] });

  useEffect(() => {
    fetchStudents();
    fetchClasses();
  }, []);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/students`);
      setStudents(response.data);
    } catch (error) {
      toast.error('Failed to fetch students');
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/classes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClasses(response.data);
    } catch (error) {
      console.error('Failed to fetch classes');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/students/upload`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        },
      });
      
      toast.success(`Successfully added ${response.data.students_added} students`);
      if (response.data.errors.length > 0) {
        response.data.errors.forEach(err => toast.warning(err));
      }
      fetchStudents();
    } catch (error) {
      toast.error('Failed to upload students');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/students`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Student added successfully');
      setShowAddDialog(false);
      setFormData({ name: '', email: '', contact: '', class_ids: [] });
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add student');
    }
  };

  const handleEditStudent = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      await axios.patch(`${API}/students/${selectedStudent.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Student updated successfully');
      setShowEditDialog(false);
      setSelectedStudent(null);
      fetchStudents();
    } catch (error) {
      toast.error('Failed to update student');
    }
  };

  const handleDeleteStudent = async (studentId) => {
    if (!window.confirm('Are you sure you want to delete this student? This action cannot be undone.')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/students/${studentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Student deleted successfully');
      fetchStudents();
    } catch (error) {
      toast.error('Failed to delete student');
    }
  };

  const openEditDialog = (student) => {
    setSelectedStudent(student);
    setFormData({
      name: student.name,
      email: student.email,
      contact: student.contact || '',
      class_ids: student.class_ids || []
    });
    setShowEditDialog(true);
  };

  const getClassNames = (classIds) => {
    if (!classIds || classIds.length === 0) return 'No Classes';
    const classNames = classIds
      .map(id => {
        const cls = classes.find(c => c.id === id);
        return cls ? `${cls.name} (${cls.semester} ${cls.year})` : null;
      })
      .filter(Boolean);
    return classNames.length > 0 ? classNames.join(', ') : 'No Classes';
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="student-management">
      <div>
        <h2 className="text-4xl font-bold text-slate-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Student Management
        </h2>
        <p className="text-slate-600 dark:text-slate-400">Add and manage students who will receive vouchers</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-slate-200 dark:border-slate-700" >
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Bulk Upload
            </CardTitle>
            <CardDescription>
              Upload Excel file with students. Format: Column A = Name, Column B = Email, Column C = Contact
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                disabled={uploading}
                data-testid="student-upload-input"
                className="max-w-md"
              />
              {uploading && (
                <div className="flex items-center gap-2 text-blue-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span className="text-sm">Uploading...</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 dark:border-slate-700" >
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Add Manually
            </CardTitle>
            <CardDescription>
              Add a single student through the form
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-violet-500 to-purple-400 text-white hover:from-violet-600 hover:to-purple-500" data-testid="add-student-button">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Student
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="add-student-dialog">
                <DialogHeader>
                  <DialogTitle>Add New Student</DialogTitle>
                  <DialogDescription>
                    Enter student details to add them to the system
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleAddStudent} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      data-testid="student-name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="student-email-input"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      placeholder="john@example.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact">Contact</Label>
                    <Input
                      id="contact"
                      data-testid="student-contact-input"
                      value={formData.contact}
                      onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                      placeholder="+1234567890"
                    />
                  </div>
                  <div>
                    <Label htmlFor="classes">Classes (Optional)</Label>
                    <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2 mt-2">
                      {classes.filter(c => !c.archived).length === 0 ? (
                        <p className="text-sm text-slate-500">No classes available</p>
                      ) : (
                        classes.filter(c => !c.archived).map(cls => (
                          <div key={cls.id} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`add-class-${cls.id}`}
                              checked={formData.class_ids.includes(cls.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFormData({ ...formData, class_ids: [...formData.class_ids, cls.id] });
                                } else {
                                  setFormData({ ...formData, class_ids: formData.class_ids.filter(id => id !== cls.id) });
                                }
                              }}
                              className="rounded border-gray-300"
                            />
                            <label htmlFor={`add-class-${cls.id}`} className="text-sm cursor-pointer">
                              {cls.name} ({cls.semester} {cls.year})
                            </label>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  <Button type="submit" data-testid="submit-student-button" className="w-full bg-gradient-to-r from-violet-500 to-purple-400 text-white">
                    Add Student
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200 dark:border-slate-700" >
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UsersIcon className="w-5 h-5" />
            Student List
          </CardTitle>
          <CardDescription>{students.length} students registered</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-800" >
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Class</TableHead>
                    <TableHead>Registered Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {students.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-slate-500 h-32">
                        No students found. Add students to get started.
                      </TableCell>
                    </TableRow>
                  ) : (
                    students.map((student) => (
                      <TableRow key={student.id} data-testid={`student-row-${student.email}`}>
                        <TableCell className="font-medium">{student.name}</TableCell>
                        <TableCell>{student.email}</TableCell>
                        <TableCell className="text-slate-600 dark:text-slate-400">{student.contact || '-'}</TableCell>
                        <TableCell className="text-slate-600 dark:text-slate-400">{getClassNames(student.class_ids)}</TableCell>
                        <TableCell>{new Date(student.created_date).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditDialog(student)}
                              data-testid={`edit-student-${student.id}`}
                            >
                              <Edit className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteStudent(student.id)}
                              data-testid={`delete-student-${student.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Student</DialogTitle>
            <DialogDescription>Update student information</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditStudent} className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-contact">Contact</Label>
              <Input
                id="edit-contact"
                value={formData.contact}
                onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit-classes">Classes</Label>
              <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2 mt-2">
                {classes.filter(c => !c.archived).length === 0 ? (
                  <p className="text-sm text-slate-500">No classes available</p>
                ) : (
                  classes.filter(c => !c.archived).map(cls => (
                    <div key={cls.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`edit-class-${cls.id}`}
                        checked={formData.class_ids.includes(cls.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({ ...formData, class_ids: [...formData.class_ids, cls.id] });
                          } else {
                            setFormData({ ...formData, class_ids: formData.class_ids.filter(id => id !== cls.id) });
                          }
                        }}
                        className="rounded border-gray-300"
                      />
                      <label htmlFor={`edit-class-${cls.id}`} className="text-sm cursor-pointer">
                        {cls.name} ({cls.semester} {cls.year})
                      </label>
                    </div>
                  ))
                )}
              </div>
            </div>
            <Button type="submit" className="w-full">
              Update Student
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudentManagement;